import { Fragment, useEffect } from "react";
import {
  ConfigurationList,
  ConfigurationPaper,
} from "~/components/configuration-items";
import { useViewer } from "~/providers/viewer";
import { useSnackbar } from "notistack";
import { getGraphQLErrors } from "~/components/ui-library";
import { SubmitHandler, useForm } from "react-hook-form";
import { capitalize, Grid } from "@mui/material";
import {
  GetWorkspaceDocument,
  GetWorkspacesDocument,
  useUpdateWorkspaceMutation,
} from "~/operations";
import { ScopeType, useScope } from "~/hooks/useScope";
import { useCanUpdateSpace } from "~/lib/iam/actions/mondoo/captain/Captain/UpdateSpace";
import { CreateFormInput } from "~/components/FormFields/types";
import {
  DescriptionField,
  DisplayNameField,
  IdField,
} from "~/components/FormFields";

type Props = {};

export function SpaceNameSettings({}: Props) {
  const { activeScope, spaceScope } = useScope();

  const targetType = activeScope?.type || ScopeType.Space;

  const { enqueueSnackbar } = useSnackbar();

  const { updateSpace } = useViewer();
  const [updateWorkspace] = useUpdateWorkspaceMutation({
    refetchQueries: [GetWorkspaceDocument, GetWorkspacesDocument],
  });
  const updateScope =
    targetType === ScopeType.Space ? updateSpace : updateWorkspace;

  const updateSpacePermission = useCanUpdateSpace(spaceScope);

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
  } = useForm<CreateFormInput>({
    mode: "onBlur",
    defaultValues: {
      name: activeScope?.name,
      description: activeScope?.description,
      id: activeScope?.id,
    },
  });

  useEffect(() => {
    reset({
      id: activeScope?.id,
      name: activeScope?.name,
      description: activeScope?.description,
    });
  }, [activeScope]);

  const handleBlur: SubmitHandler<CreateFormInput> = async (params) => {
    const { name, description } = params;
    if (!isDirty) return;

    try {
      await updateScope({
        variables: {
          input: {
            mrn: activeScope?.mrn || "",
            name,
            description,
          },
        },
      });

      enqueueSnackbar(`${capitalize(targetType)} successfully updated`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(
        getGraphQLErrors({
          error,
          errorMessage: `Failed to update ${targetType}.`,
        }),
        {
          variant: "error",
        },
      );
    }
  };

  return (
    <Fragment>
      <ConfigurationPaper>
        <ConfigurationList title="Name and Description">
          <Grid
            component="form"
            onBlur={handleSubmit(handleBlur)}
            container
            p={2}
            gap={3}
          >
            {activeScope?.type === "space" && (
              <IdField
                errors={errors}
                register={register}
                disabled
                target="space"
              />
            )}

            <DisplayNameField
              errors={errors}
              register={register}
              disabled={!updateSpacePermission}
              target={targetType}
            />

            <DescriptionField
              register={register}
              disabled={!updateSpacePermission}
              target={targetType}
            />
          </Grid>
        </ConfigurationList>
      </ConfigurationPaper>
    </Fragment>
  );
}
